import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { scheduleTask } from 'ember-lifeline';

/**
 * Holds a URL or image file shared via the Android share sheet (send-intent plugin).
 * Survives route transitions so the data persists through a login redirect.
 */
export default class ShareIntentService extends Service {
  @tracked pendingUrl: string | null = null;
  @tracked pendingImageUri: string | null = null;
  @tracked pendingImageType: string | null = null;

  /**
   * Resolves when session.setup() completes. Set by the application route
   * before transitioning to 'share', so the share component can await it
   * without delaying the drawer from appearing.
   * Defaults to an already-resolved promise for non-share / browser flows.
   */
  sessionSetupPromise: Promise<void> = Promise.resolve();

  /**
   * Resolves once the instance-initializer has finished its native
   * checkSendIntentReceived() call (or immediately on non-Capacitor builds).
   * Awaited by the application route so all navigation decisions are made
   * after both session.setup() AND the pending-intent state are known,
   * preventing the race condition that caused a competing route transition.
   */
  private _notifyReady!: (value: void) => void;
  readonly readyPromise: Promise<void> = new Promise<void>((resolve) => {
    this._notifyReady = resolve;
  });

  /** Called by the instance-initializer (in its finally block) when done. */
  notifyReady(): void {
    this._notifyReady();
  }

  /**
   * Consume the pending URL (clears it in one step).
   * The clear is scheduled to avoid a Glimmer backtracking-revalidation assertion
   * when this is called during component construction (same render computation).
   */
  consume(): string | null {
    const url = this.pendingUrl;
    scheduleTask(this, 'actions', () => {
      this.pendingUrl = null;
    });
    return url;
  }

  /**
   * Consume the pending image intent data (clears it in one step).
   */
  consumeImage(): { uri: string; type: string } | null {
    const uri = this.pendingImageUri;
    const type = this.pendingImageType;
    if (!uri) return null;
    scheduleTask(this, 'actions', () => {
      this.pendingImageUri = null;
      this.pendingImageType = null;
    });
    return { uri, type: type ?? 'image/jpeg' };
  }
}

declare module '@ember/service' {
  interface Registry {
    'share-intent': ShareIntentService;
  }
}
